import { type IWidgetDirectiveType } from '@framework/engine/schema/directives/IWidgetDirectiveType.ts';
import { isInputWidget } from '@framework/engine/schema/widgets/repository/inputs/isInputWidget.ts';
import { FormBuilderMetaFlags } from '@framework/engine/validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { type yup } from '@framework/validation/initYup.ts';
import { VALIDATION_VALUES_KEY } from '@framework/validation/live/validationValuesKey.ts';
import { isYupArraySchema } from '@framework/validation/YupArraySchema.ts';
import { minBy } from 'lodash';
import {
  type IOutcomeActionDirective,
  OutcomeActionDirectiveSchema,
} from './IOutcomeActionDirective.ts';

/**
 * Permette di aggiungere la possibilità di validare come required solo se specificato
 * dalle action di un task.
 */
export const OutcomeActionDirectiveType: IWidgetDirectiveType<IOutcomeActionDirective> =
  {
    name: 'outcomeAction',
    key: 'outcomeAction',
    allowsWidget: widget => isInputWidget(widget),
    configComponent: null,
    configSchema: OutcomeActionDirectiveSchema,
    isAlwaysApplied: true,
    formSchemaModifier: ({ widget, schema, flags, parentScope }) => {
      if (flags[FormBuilderMetaFlags.SkipValidationRules]) {
        // Se le regole di validazione non sono attive, vuol dire che
        // non è il campo "principale" del Widget, quindi non dobbiamo
        // esportarlo.
        return schema;
      }

      // Nel primo elemento del parentScope c'è il codice della dichiarazione
      const declarationId = parentScope[0]?.code;

      if (!declarationId) return schema;

      return schema.when(
        ['$outcomesConstraints', VALIDATION_VALUES_KEY],
        ([constraints, values]: any[], schema) => {
          if (!constraints) return schema;

          // Estraggo l'id dell'outcome selezionato (precedentemente impostato nei values)
          let outcomeId: string | null | undefined = values?.outcome?.id;

          // Se l'outcome non è selezionato, allora utilizzo di default quello con priorità maggiore
          if (!outcomeId) {
            outcomeId = getDefaultOutcomeKey(constraints);
          }

          if (!outcomeId) return setAsNotRequired(schema);

          const actions =
            constraints[outcomeId]?.declarations?.[declarationId] ?? [];

          if (actions.length === 0) return setAsNotRequired(schema);

          return schema;
        },
      );
    },
  };

/**
 * L'esito di deafult è quello con priorità minore (= maggiore importanza)
 */
function getDefaultOutcomeKey(constraints: any) {
  const priorityOutcome = minBy(
    Object.keys(constraints ?? {}).map(key => ({
      key,
      priority: constraints[key].priority,
    })),
    c => c.priority,
  );
  return priorityOutcome?.key;
}

/**
 * Gestisce l'impostazione del "not required" anche per gli arraySchema, che
 * vengono in realtà impostati come minimo 1 elementi quando obbligatori.
 */
function setAsNotRequired(schema: yup.AnySchema) {
  // I widget di tipo arraySchema obbligatori sono configurati con
  // un `.min(1, ...)`. Andiamo a sovrascriverlo per rimuovere il
  // constraint rendendo possibile inviare un array vuoto.
  // e.g. vedi `MultiSelectWidgetSchema.ts`
  if (isYupArraySchema(schema)) {
    return schema.min(0).notRequired();
  }
  return schema.notRequired();
}
